import { SEARCH_RESULT_KIND_ORDER, SearchResultKind, type SearchResult, type SearchResultGroup } from "@ibada/shared-types/search"

import { rankNavEntries, startsWithActionVerb } from "@/features/search/lib/nav-match"
import {
  SearchPaletteGroupKey,
  type SearchNavEntry,
  type SearchPaletteEntry,
  type SearchPaletteGroup,
  type SearchRecentEntry,
} from "@/features/search/model/search-palette-types"

/** Display order of the palette's groups. A query that opens with an action verb moves Actions first. */
export const SEARCH_PALETTE_GROUP_ORDER: readonly SearchPaletteGroupKey[] = [
  SearchPaletteGroupKey.Recents,
  SearchPaletteGroupKey.QuickActions,
  SearchPaletteGroupKey.Members,
  SearchPaletteGroupKey.Finance,
  SearchPaletteGroupKey.Activities,
  SearchPaletteGroupKey.Groups,
  SearchPaletteGroupKey.Ministries,
  SearchPaletteGroupKey.Departments,
  SearchPaletteGroupKey.Forms,
  SearchPaletteGroupKey.Assets,
  SearchPaletteGroupKey.Pages,
  SearchPaletteGroupKey.Actions,
]

/** Transactions and pledges share the Finance group. */
export const SEARCH_KIND_GROUP: Record<SearchResultKind, SearchPaletteGroupKey> = {
  [SearchResultKind.Member]: SearchPaletteGroupKey.Members,
  [SearchResultKind.Transaction]: SearchPaletteGroupKey.Finance,
  [SearchResultKind.Pledge]: SearchPaletteGroupKey.Finance,
  [SearchResultKind.Activity]: SearchPaletteGroupKey.Activities,
  [SearchResultKind.Group]: SearchPaletteGroupKey.Groups,
  [SearchResultKind.Ministry]: SearchPaletteGroupKey.Ministries,
  [SearchResultKind.Department]: SearchPaletteGroupKey.Departments,
  [SearchResultKind.Form]: SearchPaletteGroupKey.Forms,
  [SearchResultKind.Asset]: SearchPaletteGroupKey.Assets,
}

export const EMPTY_STATE_ACTIONS_LIMIT = 3
export const EMPTY_STATE_PAGES_LIMIT = 6
export const MATCHED_PAGES_LIMIT = 6
export const MATCHED_ACTIONS_LIMIT = 4

export function entryValue(kind: string, id: string, recent = false): string {
  return `${recent ? "recent:" : ""}${kind}:${id}`
}

function navToEntry(entry: SearchNavEntry): SearchPaletteEntry {
  return {
    value: entryValue(entry.kind, entry.id),
    id: entry.id,
    kind: entry.kind,
    title: entry.title,
    subtitle: entry.subtitle,
    url: entry.url,
    result: null,
    icon: entry.icon,
    recent: false,
  }
}

function resultToEntry(result: SearchResult, url: string): SearchPaletteEntry {
  return {
    value: entryValue(result.kind, result.id),
    id: result.id,
    kind: result.kind,
    title: result.title,
    subtitle: result.subtitle,
    url,
    result,
    icon: null,
    recent: false,
  }
}

function recentToEntry(entry: SearchRecentEntry): SearchPaletteEntry {
  return {
    value: entryValue(entry.kind, entry.id, true),
    id: entry.id,
    kind: entry.kind,
    title: entry.title,
    subtitle: null,
    url: entry.url,
    result: null,
    icon: null,
    recent: true,
  }
}

export type BuildPaletteGroupsInput = {
  query: string
  /** Server groups for the current term; null while nothing has been searched. */
  serverGroups: SearchResultGroup[] | null
  /** Every page the user can open (for matching while typing). */
  pages: SearchNavEntry[]
  /** The main pages offered before typing. */
  mainPages: SearchNavEntry[]
  actions: SearchNavEntry[]
  recents: SearchRecentEntry[]
  resultUrl: (result: SearchResult) => string
}

/**
 * The palette's groups, in {@link SEARCH_PALETTE_GROUP_ORDER}, empty ones left out.
 * - Before typing: recents, quick actions and the main pages, so the palette is never blank.
 * - While typing: the server's results by kind, then the pages and actions whose names match.
 */
export function buildPaletteGroups(input: BuildPaletteGroupsInput): SearchPaletteGroup[] {
  const query = input.query.trim()
  const groups = new Map<SearchPaletteGroupKey, SearchPaletteGroup>()
  const add = (key: SearchPaletteGroupKey, entries: SearchPaletteEntry[], total: number | null = null) => {
    if (entries.length === 0) return
    const existing = groups.get(key)
    if (existing) {
      existing.entries.push(...entries)
      existing.total = existing.total == null || total == null ? (existing.total ?? total) : existing.total + total
    } else {
      groups.set(key, { key, entries: [...entries], total })
    }
  }

  if (query.length === 0) {
    add(SearchPaletteGroupKey.Recents, input.recents.map(recentToEntry))
    add(SearchPaletteGroupKey.QuickActions, input.actions.slice(0, EMPTY_STATE_ACTIONS_LIMIT).map(navToEntry))
    add(SearchPaletteGroupKey.Pages, input.mainPages.slice(0, EMPTY_STATE_PAGES_LIMIT).map(navToEntry))
  } else {
    const serverGroups = [...(input.serverGroups ?? [])].sort(
      (left, right) => SEARCH_RESULT_KIND_ORDER.indexOf(left.kind) - SEARCH_RESULT_KIND_ORDER.indexOf(right.kind),
    )
    for (const group of serverGroups) {
      const key = SEARCH_KIND_GROUP[group.kind]
      if (!key) continue
      add(
        key,
        group.items.map((result) => resultToEntry(result, input.resultUrl(result))),
        group.total,
      )
    }
    add(
      SearchPaletteGroupKey.Pages,
      rankNavEntries(
        query,
        input.pages.map((page) => ({ ...page, keywords: [...page.keywords, ...(page.subtitle ? [page.subtitle] : [])] })),
      )
        .slice(0, MATCHED_PAGES_LIMIT)
        .map(navToEntry),
    )
    add(SearchPaletteGroupKey.Actions, rankNavEntries(query, input.actions).slice(0, MATCHED_ACTIONS_LIMIT).map(navToEntry))
  }

  const order = startsWithActionVerb(query)
    ? [SearchPaletteGroupKey.Actions, ...SEARCH_PALETTE_GROUP_ORDER.filter((key) => key !== SearchPaletteGroupKey.Actions)]
    : SEARCH_PALETTE_GROUP_ORDER
  return order.flatMap((key) => {
    const group = groups.get(key)
    return group ? [group] : []
  })
}
